import { defineStore } from 'pinia';
import { omit, uniqBy } from 'lodash-es';
import { ref, shallowRef } from 'vue';
import { useLocalStorage } from '@src/utils/storage';
import * as api from '@src/api/city';
import type { CityListItem, CurrentCity, CurrentCityInput } from '@src/api/city';
import {
    CITY_VIP_FIELDS,
    MAP_VIP_STATUS,
    findCityByMapId,
    normalizeCurrentCity,
    normalizeMyCityMapList,
    normalizeSystemGeoList,
} from '@src/api/city';
import { locateCityByIp } from '@src/views/Map/utils/cityGeo';
import { getFormAppInfo } from '@src/utils/subApp';

/** 城市选择面板分组：首字母 + 该组城市列表 */
interface CityGroupItem {
    key: string;
    options: CityListItem[];
}

/** 将 geo/list 按 firstWord 分组，同 mapId 去重，供 CitySelector 渲染 */
const groupCitiesByFirstWord = (cities: CityListItem[]): CityGroupItem[] => {
    const groups: Record<string, CityListItem[]> = {};
    cities.forEach(city => {
        const firstWord = city.firstWord;
        if (!firstWord) return;
        if (!groups[firstWord]) groups[firstWord] = [];
        groups[firstWord].push(city);
    });
    return Object.keys(groups)
        .sort()
        .map(key => ({
            key,
            options: uniqBy(groups[key], 'mapId'),
        }));
};

export const useStoreCity = defineStore('city', () => {
    // ---------------------------- 持久化状态 ----------------------------

    /** 全局当前城市（localStorage）；null 表示本地尚未写入（首次进入）。Map 页以 route.query 为准，不读写此字段 */
    const currentCity = useLocalStorage<CurrentCity | null>('currentCity', null);

    // ---------------------------- 内存状态（会话级） ----------------------------

    /** 城市列表 / 我的地图接口 loading */
    const isLoading = ref(false);
    /** geo/list 标准化列表，供 IP 定位 adcode 匹配 */
    const cityList = shallowRef<CityListItem[]>([]);
    /** 按首字母分组后的城市列表，供 CitySelector */
    const cityGroups = shallowRef<CityGroupItem[]>([]);
    /** 我的城市地图列表（已标准化）；超级管理员可能为空数组 */
    const myCityMapList = shallowRef<CityListItem[]>([]);

    /** 城市列表是否已拉取（同会话 dedupe） */
    let isCityListFetched = false;
    let cityListFetchPromise: Promise<CityGroupItem[]> | null = null;
    /** 我的地图是否已拉取（同会话 dedupe） */
    let isMyCityMapFetched = false;
    let myCityMapFetchPromise: Promise<CityListItem[]> | null = null;
    /** currentCity 是否已完成登录后 bootstrap（IP 定位 + resolve） */
    let isCurrentCityBootstrapped = false;
    let currentCityBootstrapPromise: Promise<void> | null = null;

    // ---------------------------- 当前城市写入 ----------------------------

    /** 用户主动切换城市（整对象替换，对齐旧版 SET_CURRENT_CITY） */
    const setCurrentCity = (city: CurrentCityInput) => {
        currentCity.value = normalizeCurrentCity(city);
    };

    // ---------------------------- 城市解析与列表拉取 ----------------------------

    /**
     * 按优先级确定并写入 currentCity。仅由 bootstrapCurrentCity 调用，Map 页不参与。
     *
     * 1. 外链 targetMapId 指定的城市（最高优先级，每次 bootstrap 重新读取）
     * 2. 本地已有城市：命中 myCityMapList 则用接口数据刷新会员信息，未命中说明已到期下线，清空会员字段
     * 3. 首次进入：定位城市命中 myCityMapList 则直接用，否则取第一个在期城市 / 首个城市，
     *    myCityMapList 为空（超管或未开通）时回落定位城市（定位内部已兜底 DEFAULT_CITY）
     *
     * 三层均以 myCityMapList 为准补全会员信息，定位与全量字典只负责定出是哪个城市。
     *
     * @param targetCity targetMapId 匹配到的城市，无外链或未匹配时为 null
     * @param geolocationCity IP 定位城市；targetCity 命中时不定位，为 null
     */
    const resolveCurrentCity = (targetCity: CityListItem | null, geolocationCity: CurrentCity | null) => {
        if (targetCity) {
            currentCity.value = normalizeCurrentCity(targetCity);
            return;
        }

        if (currentCity.value) {
            const matchedCity = findCityByMapId(currentCity.value.mapId, myCityMapList.value);
            currentCity.value = normalizeCurrentCity(matchedCity ?? omit(currentCity.value, CITY_VIP_FIELDS));
            return;
        }

        const matchedGeoCity = findCityByMapId(geolocationCity?.mapId, myCityMapList.value);
        const firstActiveCity = myCityMapList.value.find(city => city.vipStatus === MAP_VIP_STATUS.ACTIVE);
        currentCity.value = normalizeCurrentCity(matchedGeoCity ?? firstActiveCity ?? myCityMapList.value[0] ?? geolocationCity ?? {});
    };

    /** 获取全量城市列表并按首字母分组（路由守卫预加载，未登录可用，带缓存） */
    const fetchCityList = async () => {
        if (isCityListFetched) return cityGroups.value;
        if (cityListFetchPromise) return cityListFetchPromise;

        isLoading.value = true;
        cityListFetchPromise = api
            .postSystemGeoList()
            .then(({ data = [] }) => {
                cityList.value = normalizeSystemGeoList(data);
                cityGroups.value = groupCitiesByFirstWord(cityList.value);
                isCityListFetched = true;
                return cityGroups.value;
            })
            .finally(() => {
                isLoading.value = false;
                cityListFetchPromise = null;
            });

        return cityListFetchPromise;
    };

    /** 获取我的城市地图列表（超级管理员可能返回空数组，仍视为已加载；仅登录后调用） */
    const fetchMyCityMapList = async (userId: string) => {
        if (!userId) return myCityMapList.value;
        if (isMyCityMapFetched) return myCityMapList.value;
        if (myCityMapFetchPromise) return myCityMapFetchPromise;

        myCityMapFetchPromise = api
            .getMapCitymapMyVrDoorCityMapList()
            .then(({ data = [] }) => {
                myCityMapList.value = normalizeMyCityMapList(data ?? []);
                isMyCityMapFetched = true;
                return myCityMapList.value;
            })
            .finally(() => {
                myCityMapFetchPromise = null;
            });

        return myCityMapFetchPromise;
    };

    /**
     * 登录后确定 currentCity，同会话仅执行一次（内存态，刷新后重新执行）。
     * targetMapId 能直接匹配到城市时跳过 IP 定位，省去腾讯地图 SDK 加载。
     * 需在 fetchMyCityMapList 之后调用，否则匹配不到会员信息。
     */
    const bootstrapCurrentCity = async () => {
        if (isCurrentCityBootstrapped) return;
        if (currentCityBootstrapPromise) return currentCityBootstrapPromise;

        currentCityBootstrapPromise = (async () => {
            await fetchCityList();

            const { targetMapId } = getFormAppInfo();
            const targetCity = findCityByMapId(targetMapId, myCityMapList.value, cityList.value);
            const geolocationCity = targetCity ? null : await locateCityByIp(cityList.value);

            resolveCurrentCity(targetCity, geolocationCity);
            isCurrentCityBootstrapped = true;
        })().finally(() => {
            currentCityBootstrapPromise = null;
        });

        return currentCityBootstrapPromise;
    };

    /** 路由守卫 / 登录成功入口：并行拉取全量城市列表与我的地图，再确定 currentCity */
    const initializeCityData = async (userId: string) => {
        await Promise.all([fetchCityList(), fetchMyCityMapList(userId)]);
        await bootstrapCurrentCity();
    };

    /** 登出时清空内存缓存与持久化城市上下文 */
    const resetCityState = () => {
        cityList.value = [];
        cityGroups.value = [];
        myCityMapList.value = [];
        currentCity.value = null;
        isCityListFetched = false;
        cityListFetchPromise = null;
        isMyCityMapFetched = false;
        myCityMapFetchPromise = null;
        isCurrentCityBootstrapped = false;
        currentCityBootstrapPromise = null;
        isLoading.value = false;
    };

    return {
        isLoading,
        cityList,
        cityGroups,
        myCityMapList,
        currentCity,
        fetchCityList,
        fetchMyCityMapList,
        initializeCityData,
        setCurrentCity,
        resetCityState,
    };
});
